import { Controller } from 'stimulus'

export default class extends Controller {
  static targets = [
    'hidden', 'summary', 'clearButton', 'modal',
    'dateFilter', 'dateToFilter', 'titleFilter', 'cinemaFilter', 'cityFilter', 'theatreFilter', 'statusFilter',
    'results', 'prev', 'next', 'pageinfo'
  ]

  static values = { searchUrl: String }

  connect() {
    this.page = 1
    this.pages = 1
    this.items = []
    this.debounceTimer = null
    this.abortController = null
    this._onKeydown = this.handleKeydown.bind(this)
  }

  disconnect() {
    this.cancelDebounce()
    this.abortFetch()
    document.removeEventListener('keydown', this._onKeydown)
  }

  open(event) {
    if (event) event.preventDefault()
    this.modalTarget.hidden = false
    document.addEventListener('keydown', this._onKeydown)
    this.setDefaultDate()
    this.resetAndSearch()
  }

  close(event) {
    if (event) event.preventDefault()
    this.modalTarget.hidden = true
    document.removeEventListener('keydown', this._onKeydown)
  }

  handleKeydown(event) {
    if (event.key === 'Escape') this.close()
  }

  setDefaultDate() {
    if (!this.hasDateFilterTarget) return
    if (this.dateFilterTarget.value) return

    const today = this.todayString()
    const fp = this.flatpickrInstance(this.dateFilterTarget)
    if (fp) {
      fp.setDate(today, false)
    }
    // Всегда пишем в input: flatpickr может ещё не успеть инициализироваться
    // в скрытой модалке, а бэкенд ждёт date в query.
    this.dateFilterTarget.value = today
  }

  todayString() {
    const today = new Date()
    const y = today.getFullYear()
    const m = String(today.getMonth() + 1).padStart(2, '0')
    const d = String(today.getDate()).padStart(2, '0')
    return `${y}-${m}-${d}`
  }

  resetAndSearch() {
    this.page = 1
    this.search()
  }

  resetAndSearchDebounced() {
    this.cancelDebounce()
    this.debounceTimer = setTimeout(() => this.resetAndSearch(), 350)
  }

  async search() {
    this.showLoading()

    try {
      this.abortFetch()
      this.abortController = new AbortController()

      const url = new URL(this.searchUrlValue, window.location.origin)
      url.searchParams.set('page', this.page)
      this.appendParam(url, 'date', this.value('dateFilter'))
      this.appendParam(url, 'date_to', this.value('dateToFilter'))
      this.appendParam(url, 'title', this.value('titleFilter'))
      this.appendParam(url, 'cinema', this.value('cinemaFilter'))
      this.appendParam(url, 'city', this.value('cityFilter'))
      this.appendParam(url, 'theatre', this.value('theatreFilter'))
      this.appendParam(url, 'status', this.value('statusFilter'))

      const response = await fetch(url, {
        signal: this.abortController.signal,
        headers: { Accept: 'application/json' }
      })

      if (!response.ok) throw new Error('Search failed')

      const data = await response.json()
      this.items = data.screenings || []
      this.pages = (data.pagination && data.pagination.pages) || 1
      this.page = (data.pagination && data.pagination.page) || 1
      this.count = (data.pagination && data.pagination.count) || 0
      this.renderResults()
      this.renderPagination()
    } catch (error) {
      if (error.name !== 'AbortError') this.renderError()
    }
  }

  renderResults() {
    if (this.items.length === 0) {
      this.resultsTarget.innerHTML = '<tr><td colspan="6" class="screening-modal__empty">Ничего не найдено</td></tr>'
      return
    }

    this.resultsTarget.innerHTML = this.items.map((item, index) => {
      const titleBlock = [
        `<div class="screening-modal__title-name">${this.escape(item.title_name)}</div>`,
        item.theatre_name ? `<div class="screening-modal__sub">Театр: ${this.escape(item.theatre_name)}</div>` : ''
      ].join('')

      const cinemaBlock = [
        `<div>${this.escape(item.cinema_name)}</div>`,
        item.hall_name ? `<div class="screening-modal__sub">Зал: ${this.escape(item.hall_name)}</div>` : ''
      ].join('')

      const cityBlock = [
        `<div>${this.escape(item.city_name)}</div>`,
        item.country_name ? `<div class="screening-modal__sub">${this.escape(item.country_name)}</div>` : ''
      ].join('')

      return `<tr>
        <td><div>${this.escape(item.date)}</div><div class="screening-modal__sub">${this.escape(item.weekday)}</div></td>
        <td>${this.escape(item.time)}</td>
        <td>${titleBlock}</td>
        <td>${cinemaBlock}</td>
        <td>${cityBlock}</td>
        <td class="text-right">
          <button type="button" class="btn btn-sm btn-primary"
            data-action="admin--screening-picker#selectItem" data-index="${index}">Выбрать</button>
        </td>
      </tr>`
    }).join('')
  }

  renderPagination() {
    if (this.hasPageinfoTarget) {
      this.pageinfoTarget.textContent = this.count
        ? `Стр. ${this.page} из ${this.pages} · всего ${this.count}`
        : ''
    }
    if (this.hasPrevTarget) this.prevTarget.disabled = this.page <= 1
    if (this.hasNextTarget) this.nextTarget.disabled = this.page >= this.pages
  }

  prevPage() {
    if (this.page > 1) {
      this.page -= 1
      this.search()
    }
  }

  nextPage() {
    if (this.page < this.pages) {
      this.page += 1
      this.search()
    }
  }

  selectItem(event) {
    const item = this.items[parseInt(event.currentTarget.dataset.index, 10)]
    if (item) this.applySelection(item)
    this.close()
  }

  applySelection(item) {
    this.hiddenTarget.value = item.id
    this.updateSummary(item)
    this.prefillFilm(item)
    this.prefillDate(item)
    this.prefillPlace(item)
    this.prefillCity(item)
    this.prefillGuests(item)
    this.prefillWidgetTicketLink(item)
    this.prefillButtonName(item)
    this.prefillStatus(item)
  }

  updateSummary(item) {
    if (!this.hasSummaryTarget) return
    const main = `${this.escape(item.date)} ${this.escape(item.time)} · ${this.escape(item.title_name)}`
    const metaParts = [item.cinema_name, item.city_name].filter(Boolean).map((p) => this.escape(p))
    metaParts.push(`ID сеанса: ${item.id}`)
    this.summaryTarget.innerHTML =
      `<div class="screening-picker__summary-main">${main}</div>` +
      `<div class="screening-picker__summary-meta">${metaParts.join(' · ')}</div>`
    this.summaryTarget.hidden = false
    if (this.hasClearButtonTarget) this.clearButtonTarget.hidden = false
  }

  prefillFilm(item) {
    if (!item.title_id) {
      this.clearFilm()
      return
    }
    const form = this.form
    if (!form) return

    const hidden = form.querySelector('input[name="banner[film_id]"]')
    if (hidden) hidden.value = item.title_id

    const input = form.querySelector('[data-admin--film-typeahead-target="input"]')
    if (input && item.title_name) input.value = item.title_name

    const clearBtn = form.querySelector('[data-admin--film-typeahead-target="clearButton"]')
    if (clearBtn) clearBtn.hidden = false
  }

  prefillDate(item) {
    if (!item.starts_at) {
      this.clearDate()
      return
    }
    const form = this.form
    if (!form) return

    const input = form.querySelector('input[name="banner[local_starts_at]"]')
    if (!input) return

    // Всегда пишем value: flatpickr setDate может не обновить input
    // (другой инстанс / wrap), а на сервер уходит именно value.
    input.value = item.starts_at

    const fp = this.flatpickrInstance(input)
    if (fp) {
      fp.setDate(item.starts_at, true)
    }
  }

  prefillPlace(item) {
    const form = this.form
    if (!form) return
    const place = form.querySelector('input[name="banner[place]"]')
    if (!place) return

    place.value = item.cinema_name || ''
  }

  prefillCity(item) {
    const form = this.form
    if (!form) return

    const container = form.querySelector('#banner_cities')
    if (!container) return

    this.clearCityRows(container)

    if (!item.city_id) return

    const addLink = form.querySelector('a.add_fields[data-association="banner_city"]')
    if (!addLink) return

    addLink.click()
    const row = [...container.querySelectorAll('.nested-fields')].pop()
    if (row) this.setCity(row, item)
  }

  clearCityRows(container) {
    container.querySelectorAll('.nested-fields').forEach((row) => {
      const removeLink = row.querySelector('a.remove_fields')
      if (removeLink) {
        removeLink.click()
      } else {
        row.remove()
      }
    })
  }

  setCity(row, item) {
    const select = row.querySelector('select[name*="[city_id]"]')
    if (!select) return

    const cityId = String(item.city_id)
    const apply = () => {
      if (!window.jQuery) return false
      const $select = window.jQuery(select)
      if (!$select.data('selectpicker')) return false

      $select.selectpicker('val', cityId)
      return true
    }

    if (!apply()) {
      requestAnimationFrame(() => {
        if (!apply()) setTimeout(() => apply(), 50)
      })
    }
  }

  prefillGuests(item) {
    const form = this.form
    if (!form) return

    const tbody = form.querySelector('#banner_guests')
    const addLink = form.querySelector('a.add_fields[data-association="banner_guest"]')
    if (!tbody || !addLink) return

    this.clearGuestRows(tbody)

    const guests = (item.guests || []).filter((guest) => guest.id)
    guests.forEach((guest) => {
      addLink.click()
      const row = tbody.lastElementChild
      if (row) this.setGuestPerson(row, guest)
    })
  }

  prefillButtonName(item) {
    const form = this.form
    if (!form) return
    const input = form.querySelector('input[name="banner[button_name]"]')
    if (input) input.value = item.button_name
  }

  prefillStatus(item) {
    const form = this.form
    if (!form) return
    const input = form.querySelector('input[name="banner[status]"]')
    if (input) input.value = item.status
  }

  clearGuestRows(tbody) {
    tbody.querySelectorAll('tr.nested-fields').forEach((row) => {
      const removeLink = row.querySelector('a.remove_fields')
      if (removeLink) {
        removeLink.click()
      } else {
        row.remove()
      }
    })
  }

  setGuestPerson(row, guest) {
    const select = row.querySelector('select[name*="[person_id]"]')
    if (!select) return

    const apply = () => {
      if (!window.jQuery) return false
      const $select = window.jQuery(select)
      if (!$select.hasClass('select2-hidden-accessible')) return false

      if ($select.find(`option[value="${guest.id}"]`).length === 0) {
        const option = new Option(guest.name || String(guest.id), guest.id, true, true)
        $select.append(option)
      }
      $select.val(guest.id).trigger('change')
      return true
    }

    if (!apply()) {
      requestAnimationFrame(() => {
        if (!apply()) setTimeout(() => apply(), 50)
      })
    }
  }

  prefillWidgetTicketLink(item) {
    const link = item.widget_link || item.registration_link
    if (!link) {
      this.clearWidgetTicketLink()
      return
    }
    const form = this.form
    if (!form) return
    const input = form.querySelector('input[name="banner[button_link]"]')
    if (input) input.value = link
  }

  clear(event) {
    if (event) event.preventDefault()
    this.hiddenTarget.value = ''
    if (this.hasSummaryTarget) {
      this.summaryTarget.hidden = true
      this.summaryTarget.innerHTML = ''
    }
    if (this.hasClearButtonTarget) this.clearButtonTarget.hidden = true
    this.clearPrefilledFields()
  }

  clearPrefilledFields() {
    this.clearFilm()
    this.clearDate()
    this.clearPlace()
    this.clearCity()
    this.clearGuests()
    this.clearWidgetTicketLink()
  }

  clearFilm() {
    const form = this.form
    if (!form) return

    const hidden = form.querySelector('input[name="banner[film_id]"]')
    if (hidden) hidden.value = ''

    const input = form.querySelector('[data-admin--film-typeahead-target="input"]')
    if (input) input.value = ''

    const clearBtn = form.querySelector('[data-admin--film-typeahead-target="clearButton"]')
    if (clearBtn) clearBtn.hidden = true
  }

  clearDate() {
    const form = this.form
    if (!form) return

    const input = form.querySelector('input[name="banner[local_starts_at]"]')
    if (!input) return

    const fp = this.flatpickrInstance(input)
    if (fp) {
      fp.clear()
    } else {
      input.value = ''
    }
  }

  clearPlace() {
    const form = this.form
    if (!form) return
    const place = form.querySelector('input[name="banner[place]"]')
    if (place) place.value = ''
  }

  clearCity() {
    const form = this.form
    if (!form) return
    const container = form.querySelector('#banner_cities')
    if (container) this.clearCityRows(container)
  }

  clearGuests() {
    const form = this.form
    if (!form) return
    const tbody = form.querySelector('#banner_guests')
    if (tbody) this.clearGuestRows(tbody)
  }

  clearWidgetTicketLink() {
    const form = this.form
    if (!form) return
    const input = form.querySelector('input[name="banner[button_link]"]')
    if (input) input.value = ''
  }

  // Helpers

  flatpickrInstance(input) {
    const wrapper = input.closest('[data-controller~="flatpickr"]')
    if (wrapper) {
      if (wrapper._flatpickr) return wrapper._flatpickr

      const controller = this.application.getControllerForElementAndIdentifier(wrapper, 'flatpickr')
      if (controller && controller.fp) return controller.fp
    }
    if (input._flatpickr) return input._flatpickr
    return null
  }

  get form() {
    return this.element.closest('form')
  }

  value(name) {
    const target = `${name}Target`
    const has = `has${name.charAt(0).toUpperCase()}${name.slice(1)}Target`
    return this[has] ? this[target].value.trim() : ''
  }

  appendParam(url, key, val) {
    if (val) url.searchParams.set(key, val)
  }

  showLoading() {
    this.resultsTarget.innerHTML = '<tr><td colspan="6" class="screening-modal__empty">Поиск…</td></tr>'
  }

  renderError() {
    this.resultsTarget.innerHTML = '<tr><td colspan="6" class="screening-modal__empty">Ошибка поиска</td></tr>'
  }

  cancelDebounce() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = null
    }
  }

  abortFetch() {
    if (this.abortController) {
      this.abortController.abort()
      this.abortController = null
    }
  }

  escape(text) {
    if (text === null || text === undefined) return ''
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }
}
